import axios, { AxiosHeaders } from "axios";
import { LOCAL_STORAGE_KEYS } from "@/config/constants";
import type { AppAccessMap } from "@/lib/appAccess";

/**
 * Instância base do Axios.
 * Em desenvolvimento, o proxy do Vite encaminha /api → http://localhost:3001.
 * Em produção, o Express serve o frontend estático e /api resolve nativamente.
 *
 * Autenticação dupla:
 *  - Cookie bi.sid (quando o browser aceita cookies)
 *  - Header X-Session-Token (fallback: token assinado armazenado no localStorage)
 */
const api = axios.create({
  baseURL: "/",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Injeta o token de sessão em toda requisição como fallback ao cookie
api.interceptors.request.use(
  (config) => {
    /* FormData: não enviar Content-Type: application/json (default da instância) —
     * o browser define multipart/form-data com boundary; senão o multer no servidor
     * não recebe arquivo nem campos (400 em /photo). */
    if (config.data instanceof FormData) {
      const h = config.headers;
      if (h instanceof AxiosHeaders) {
        h.delete("Content-Type");
        h.delete("content-type");
      } else if (h && typeof h === "object") {
        const rec = h as Record<string, unknown>;
        delete rec["Content-Type"];
        delete rec["content-type"];
      }
    }
    try {
      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
      if (token) {
        // Axios v1.x: config.headers pode ser AxiosHeaders ou objeto simples
        if (config.headers) {
          config.headers["X-Session-Token"] = token;
        }
      }
    } catch {
      // localStorage pode estar bloqueado em alguns contextos (Safari privado, etc.)
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const SESSION_EXPIRED_ERRORS = new Set(["Nao autenticado.", "Sessao invalida."]);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url: string = (error.config?.url as string) ?? "";
      // /auth/me e /auth/login: AuthContext trata localmente.
      if (url.includes("/auth/me") || url.includes("/auth/login")) {
        return Promise.reject(error);
      }
      const apiError = String(
        (error.response?.data as { error?: string } | undefined)?.error ?? "",
      );
      // Só desloga quando o middleware de sessão SEMOG rejeita a requisição.
      // Erros 401 de APIs externas (ex.: Uazapi/WhatsApp) não devem encerrar a sessão.
      if (SESSION_EXPIRED_ERRORS.has(apiError)) {
        window.dispatchEvent(new CustomEvent("semog:unauthorized"));
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/* ─── Tipos de resposta da API ──────────────────────────────────────────── */

export interface ApiUser {
  id: string;
  username: string;
  displayName: string;
  role: string;
  isTi?: boolean;
  isMaster?: boolean;
  siengeFullAccess?: boolean;
  groupIds?: string[];
  /** @deprecated Use groupIds */
  groupId?: string | null;
  /** null = todas as obras; [] = nenhuma atribuída. */
  costCenterIds?: string[] | null;
  appAccess?: AppAccessMap;
  permissions?: Record<string, Record<string, boolean>>;
  /** Vinculado a operador de contrato ativo — acesso operacional a Locações. */
  locacoesOperador?: boolean;
}

export interface LoginResponse {
  success: boolean;
  user?: ApiUser;
  sessionToken?: string;
  error?: string;
}

export interface MeResponse {
  success: boolean;
  user?: ApiUser;
  authDisabled?: boolean;
  error?: string;
}

/* ─── Endpoints de autenticação ─────────────────────────────────────────── */

export const authApi = {
  login: (username: string, password: string) =>
    api.post<LoginResponse>("/api/auth/login", { username, password }),

  logout: () =>
    api.post("/api/auth/logout"),

  me: () =>
    api.get<MeResponse>("/api/auth/me"),

  listUsers: () =>
    api.get<{
      success: boolean;
      users: Array<{
        id: string;
        username: string;
        displayName: string;
        role: string;
        isTi: boolean;
        groupIds: string[];
        groupId: string | null;
        costCenterIds: string[] | null;
        phone?: string;
        locacoesOperador?: boolean;
        isOperadorOnly?: boolean;
        operadorCatalogId?: string | null;
        operadorCatalogNome?: string | null;
        operadorContratosAtivos?: number;
      }>;
    }>("/api/auth/users"),

  createUser: (body: {
    username: string;
    password: string;
    displayName: string;
    role?: string;
    groupIds?: string[];
    groupId?: string | null;
    costCenterIds?: string[];
    phone?: string;
    isTi?: boolean;
    operadorCatalogId?: string | null;
  }) => api.post<{ success: boolean; user?: ApiUser; error?: string }>("/api/auth/users", body),

  updateUser: (
    id: string,
    body: {
      displayName?: string;
      password?: string;
      role?: string;
      groupIds?: string[];
      groupId?: string | null;
      costCenterIds?: string[] | null;
      phone?: string | null;
      isTi?: boolean;
      operadorCatalogId?: string | null;
    },
  ) => api.patch<{ success: boolean; user?: ApiUser; error?: string }>(`/api/auth/users/${id}`, body),

  deleteUser: (id: string) => api.delete<{ success: boolean; error?: string }>(`/api/auth/users/${id}`),

  listOperadoresCatalog: () =>
    api.get<{
      success: boolean;
      operadores: import("@/services/locacoes").OperadorCatalogDto[];
      error?: string;
    }>("/api/auth/operadores-catalog"),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post<{ success: boolean; error?: string }>("/api/auth/change-password", body),
};

export interface AccessGroupDto {
  id: string;
  name: string;
  access: AppAccessMap;
}

export const accessAdminApi = {
  /** Preferir /api/auth/access — compatível com proxies que só roteiam /api/auth. */
  listGroups: () => api.get<{ success: boolean; groups: AccessGroupDto[] }>("/api/auth/access/groups"),

  createGroup: (body: { name: string; access: Partial<AppAccessMap> }) =>
    api.post<{ success: boolean; group: AccessGroupDto }>("/api/auth/access/groups", body),

  updateGroup: (id: string, body: { name?: string; access?: Partial<AppAccessMap> }) =>
    api.patch<{ success: boolean; group: AccessGroupDto }>(`/api/auth/access/groups/${id}`, body),

  deleteGroup: (id: string) =>
    api.delete<{ success: boolean; error?: string }>(`/api/auth/access/groups/${id}`),
};

export interface WhatsappStatusDto {
  success: boolean;
  configured?: boolean;
  connected?: boolean;
  loggedIn?: boolean;
  status?: string;
  jid?: string | null;
  profileName?: string;
  profilePicUrl?: string;
  qrcode?: string;
  paircode?: string;
  config?: {
    instanceName: string;
    instanceId: string;
    createdAt: string;
    updatedAt: string;
  };
  message?: string;
  warning?: string;
  error?: string;
}

export const whatsappApi = {
  status: () => api.get<WhatsappStatusDto>("/api/whatsapp"),

  setup: () => api.post<{ success: boolean; message?: string; error?: string }>("/api/whatsapp/setup"),

  connect: () => api.post<WhatsappStatusDto>("/api/whatsapp/connect"),

  disconnect: () => api.post<{ success: boolean; message?: string; error?: string }>("/api/whatsapp/disconnect"),

  send: (number: string, text: string) =>
    api.post<{ success: boolean; error?: string }>("/api/whatsapp/send", { number, text }),
};
